import { useEffect } from 'react';

type PageSeoOptions = {
  title: string;
  description: string;
  path: string;
  keywords: string[];
  language?: 'en-MY' | 'ms-MY';
  schema: Record<string, unknown> | Record<string, unknown>[];
  image?: string;
  ogType?: 'website' | 'article';
};

const SITE_URL = 'https://www.mrcuci.me';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

function setMeta(name: string, content: string, property = false) {
  const attribute = property ? 'property' : 'name';
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${name}"]`
  );

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  element.content = content;
}

function setAlternate(hreflang: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${hreflang}"]`
  );

  if (!element) {
    element = document.createElement('link');
    element.rel = 'alternate';
    element.hreflang = hreflang;
    document.head.appendChild(element);
  }

  element.href = href;
}

export function usePageSeo({
  title,
  description,
  path,
  keywords,
  language = 'en-MY',
  schema,
  image = DEFAULT_IMAGE,
  ogType = 'website',
}: PageSeoOptions) {
  const keywordContent = keywords.join(', ');
  const schemaContent = JSON.stringify(schema);

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${path}`;
    const previousTitle = document.title;
    const previousLanguage = document.documentElement.lang;
    const canonical =
      document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
      document.head.appendChild(document.createElement('link'));
    const schemaScript = document.createElement('script');

    canonical.rel = 'canonical';
    canonical.href = canonicalUrl;
    document.title = title;
    document.documentElement.lang = language;

    setMeta('description', description);
    setMeta('keywords', keywordContent);
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('geo.region', 'MY');
    setMeta('geo.placename', 'Malaysia');
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', ogType, true);
    setMeta('og:locale', language === 'ms-MY' ? 'ms_MY' : 'en_MY', true);
    setMeta('og:image', image, true);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    setMeta('twitter:card', 'summary_large_image');

    setAlternate('en-MY', canonicalUrl);
    setAlternate('ms-MY', canonicalUrl);
    setAlternate('x-default', canonicalUrl);

    schemaScript.type = 'application/ld+json';
    schemaScript.dataset.pageSchema = 'true';
    schemaScript.text = schemaContent;
    document.head
      .querySelectorAll('script[data-page-schema="true"]')
      .forEach((element) => element.remove());
    document.head.appendChild(schemaScript);

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLanguage;
      schemaScript.remove();
    };
  }, [description, image, keywordContent, language, ogType, path, schemaContent, title]);
}
